import { useLayoutEffect, useRef, useState, type ReactNode } from "react";

export type RevealProps = {
  /** True: unfolds and fades in. False: folds and fades out, then unmounts. Reduced motion: it just appears / goes. */
  shown: boolean;
  children?: ReactNode;
  testID?: string;
};

/**
 * Enter / exit motion for something that comes and goes in the layout (a banner above the shell): it takes its height
 * as it fades in and gives it back as it fades out, so what's below slides instead of jumping. It stays mounted until
 * the exit animation ends (or at once when there is none: reduced motion). iOS: `.transition(.move(edge: .top)
 * .combined(with: .opacity))`; Android: `AnimatedVisibility(expandVertically() + fadeIn())`.
 */
export function Reveal({ shown, children, testID }: RevealProps) {
  const [mounted, setMounted] = useState(shown);
  const ref = useRef<HTMLDivElement>(null);
  if (shown && !mounted) setMounted(true);
  useLayoutEffect(() => {
    const el = ref.current;
    if (shown || el === null) return;
    // No exit animation to wait for (reduced motion, or never painted): gone now.
    if (getComputedStyle(el).animationName === "none" || el.getAnimations().length === 0) setMounted(false);
  }, [shown]);
  if (!mounted) return null;
  return (
    <div
      ref={ref}
      className="b-unfold"
      data-state={shown ? "open" : "closed"}
      data-testid={testID}
      onAnimationEnd={(e) => {
        if (e.target === e.currentTarget && !shown) setMounted(false);
      }}
    >
      <div className="b-unfold-inner">{children}</div>
    </div>
  );
}
